import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { AgreementReadModel } from '@client/api';
import {
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toIntervalEndDateString,
  toIntervalStartDateString,
  toMoneyStringOrUndefined,
} from '@client/toolkit';

import { AgreementsPaths } from '../../agreements.router';
import { useAgreementPartiesNumerationText } from '../../hooks/use-agreement-parties-numeration-text';
import { colors } from '../../../../styles/color-tokens';

interface AgreementListTableRowProps {
  agreement?: AgreementReadModel;
  isFetching?: boolean;
}

export function AgreementListTableRow({ agreement, isFetching }: AgreementListTableRowProps) {
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const navigate = useNavigate();

  const owners = useAgreementPartiesNumerationText(agreement, 'Owner');
  const renters = useAgreementPartiesNumerationText(agreement, 'Tenant');

  const handleClick = useCallback(() => {
    if (!agreement?.id) return;

    navigate(generatePath(AgreementsPaths.Agreement, { id: agreement.id }));
  }, [agreement?.id, navigate]);

  return (
    <TableRow hover={!!agreement?.id} onClick={handleClick}>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(renters)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {renters}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(owners)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {owners}
        </Text>
      </TableCell>
      <TableCell>
        <Text className="text-brand-primary" loading={isFetching} isEmpty={!isDefinedString(agreement?.name)}>
          {agreement?.name}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalStartDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalStartDateString(agreement?.duration)}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalEndDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalEndDateString(agreement?.duration)}
        </Text>
      </TableCell>
      <TableCell>
        <Text isEmpty={!isDefinedString(toMoneyStringOrUndefined(agreement?.totalYearlyAmountProRated))} loading={isFetching}>
          {toMoneyStringOrUndefined(agreement?.totalYearlyAmountProRated)}
        </Text>
      </TableCell>
      <TableCell>
        <Text
          isEmpty={!isDefinedString(agreement?.status)}
          loading={isFetching}
          className={agreement?.status === 'Effective' ? `text-[${colors.status.success}]` : undefined}
        >
          {tType(`AgreementStatus.${agreement?.status}`)}
        </Text>
      </TableCell>
    </TableRow>
  );
}
